import React, { useCallback, useEffect, useState } from 'react';
import { formatAmount, money } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  KpiTile,
  PageContainer,
  PageHeader,
  Spinner,
  Textarea,
  Timeline,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { purchaseOrderPath, goodsReceiptPath } from '../../lib/slug';
import { receivingApi, grnStatusMeta, grnDiscrepancyMeta, discrepancyReasonLabel } from './receivingApi';
import { makeAttachmentsApi } from '../attachments/attachmentsApi';
import CommentComposer from '../comments/CommentComposer';
import { buildCommentTimelineItem, useCommentAnchor } from '../comments/CommentTimeline';
import { useAuth } from '../../auth/AuthContext';
import '../requisitions/requisitions.css';

const lineValue = (l) => (l.unitPrice ? l.unitPrice.currency + ' ' + formatAmount(Number(l.unitPrice.amount) * Number(l.receivedQuantity)) : '—');

const ACTIVITY_META = {
  Posted: { tone: 'success', icon: <Icon.ArrowDownCircle size={14} /> },
  DiscrepancyFlagged: { tone: 'danger', icon: <Icon.CircleAlert size={14} /> },
  DiscrepancyResolved: { tone: 'success', icon: <Icon.Check size={14} /> },
  Notified: { tone: 'brand', icon: <Icon.Mail size={14} /> },
  Cancelled: { tone: 'warning', icon: <Icon.Ban size={14} /> },
};

export default function GoodsReceiptDetailPage() {
  const { id: routeRef } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { hasPermission } = useProperty();
  const { user } = useAuth();
  const canResolve = hasPermission('receiving.record');
  const canReturn = hasPermission('returns.manage');

  const [grn, setGrn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);
  const [activity, setActivity] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const anchorId = useCommentAnchor(activity);
  const [busy, setBusy] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [linePhotos, setLinePhotos] = useState({}); // { grnLineId: [{ id, fileName, url }] }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setGrn(await receivingApi.get(routeRef));
    } catch (err) {
      setError(err?.message || t('Goods receipt not found.'));
    } finally {
      setLoading(false);
    }
  }, [routeRef, t]);

  const loadActivity = useCallback(async (id) => {
    try {
      const res = await receivingApi.listActivity(id, { pageSize: 100 });
      setActivity(res.items || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { if (grn?.id) loadActivity(grn.id); }, [grn?.id, loadActivity]);

  // Per-line delivery photos, with presigned thumbnail URLs.
  useEffect(() => {
    if (!grn?.id) return undefined;
    let active = true;
    (async () => {
      try {
        const attApi = makeAttachmentsApi(`/goods-receipts/${grn.id}/attachments`);
        const atts = (await attApi.list()) || [];
        const map = {};
        await Promise.all(
          atts
            .filter((a) => a.lineId)
            .map(async (a) => {
              let url = null;
              try { url = (await attApi.downloadUrl(a.id))?.url || null; } catch { /* thumbnail is optional */ }
              (map[a.lineId] = map[a.lineId] || []).push({ id: a.id, fileName: a.fileName, url });
            })
        );
        if (active) setLinePhotos(map);
      } catch {
        if (active) setLinePhotos({});
      }
    })();
    return () => { active = false; };
  }, [grn?.id]);

  useEffect(() => {
    let active = true;
    catalogApi.listUnits().then((u) => { if (active) setUnits(u || []); }).catch(() => {});
    catalogApi.listLocations().then((l) => { if (active) setLocations(l || []); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!grn?.id) return;
    const canonical = goodsReceiptPath(grn);
    if (`/receiving/${routeRef}` !== canonical) navigate(canonical, { replace: true });
  }, [grn, routeRef, navigate]);

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;
  const locationName = (lid) => locations.find((l) => l.id === lid)?.name;

  const resolveDiscrepancy = async () => {
    setResolving(true);
    try {
      const updated = await receivingApi.resolveDiscrepancy(grn.id, resolveNote.trim() || null);
      setGrn(updated);
      setResolveNote('');
      loadActivity(grn.id);
      toast.success(t('Discrepancy marked as resolved'));
    } catch (err) {
      toast.error(t('Could not resolve discrepancy'), { description: err?.message });
    } finally {
      setResolving(false);
    }
  };

  const doPost = async () => {
    setBusy(true);
    try {
      const posted = await receivingApi.post(grn.id);
      setPostOpen(false);
      setGrn(posted);
      loadActivity(grn.id);
      toast.success(t('Goods receipt {number} posted', { number: posted.number }));
    } catch (err) {
      toast.error(t('Could not post receipt'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const doDiscard = async () => {
    setBusy(true);
    try {
      await receivingApi.remove(grn.id);
      toast.success(t('Draft discarded'));
      navigate('/receiving');
    } catch (err) {
      toast.error(t('Could not discard draft'), { description: err?.message });
      setBusy(false);
    }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const cancelled = await receivingApi.cancel(grn.id, cancelReason.trim() || null);
      setCancelOpen(false);
      setCancelReason('');
      setGrn(cancelled);
      loadActivity(grn.id);
      toast.success(t('Receipt cancelled and reversed'));
    } catch (err) {
      toast.error(t('Could not cancel receipt'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }
  if (error || !grn) {
    return (
      <PageContainer size="full">
        <EmptyState icon={<Icon.CircleAlert size={28} />} title={t("Couldn't load goods receipt")} description={error || t('Not found.')}
          action={<Button variant="secondary" onClick={() => navigate('/receiving')}>{t('Back to receiving')}</Button>} />
      </PageContainer>
    );
  }

  const statusMeta = grnStatusMeta(grn.status);
  const isDraft = grn.status === 'Draft';
  const isPosted = grn.status === 'Posted';
  const isCancelled = grn.status === 'Cancelled';

  const printButton = !isDraft ? (
    <Button variant="ghost" size="sm" leadingIcon={<Icon.Printer size={14} />} onClick={() => navigate(goodsReceiptPath(grn, '/print'))}>
      {t('Print')}
    </Button>
  ) : null;

  const headerActions = canResolve ? (
    isDraft ? (
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <Button variant="ghost" size="sm" leadingIcon={<Icon.Trash size={14} />} onClick={() => setDiscardOpen(true)}>{t('Discard')}</Button>
        <Button variant="secondary" size="sm" leadingIcon={<Icon.Pencil size={14} />} onClick={() => navigate(goodsReceiptPath(grn, '/edit'))}>{t('Edit')}</Button>
        <Button variant="primary" size="sm" leadingIcon={<Icon.ArrowDownCircle size={14} />} onClick={() => setPostOpen(true)}>{t('Post receipt')}</Button>
      </div>
    ) : isPosted ? (
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        {printButton}
        {canReturn && grn.purchaseOrderId && (
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<Icon.ArrowUpCircle size={14} />}
            onClick={() => navigate(purchaseOrderPath({ number: grn.purchaseOrderNumber, id: grn.purchaseOrderId }, `/return?grn=${grn.id}`))}
          >
            {t('Return goods')}
          </Button>
        )}
        <Button variant="destructive" size="sm" leadingIcon={<Icon.Ban size={14} />} onClick={() => setCancelOpen(true)}>{t('Cancel receipt')}</Button>
      </div>
    ) : (
      printButton
    )
  ) : printButton;

  const overviewItems = [
    { term: t('Status'), description: <Badge tone={statusMeta.tone} dot>{t(statusMeta.label)}</Badge> },
    {
      term: t('Purchase order'),
      description: grn.purchaseOrderNumber
        ? <Button variant="link" size="sm" onClick={() => navigate(purchaseOrderPath({ number: grn.purchaseOrderNumber, id: grn.purchaseOrderId }))}>{grn.purchaseOrderNumber}</Button>
        : '—',
    },
    { term: t('Supplier'), description: grn.supplierName || '—' },
    { term: t('Received into'), description: locationName(grn.locationId) || '—' },
    { term: t('Received date'), description: grn.receivedDate ? new Date(grn.receivedDate).toLocaleDateString() : '—' },
    { term: t('Delivery note'), description: grn.deliveryNote || '—' },
    { term: t('Received by'), description: grn.receivedByName || '—' },
    { term: t('Recorded'), description: new Date(grn.createdAt).toLocaleString() },
  ];

  const renderLine = (l) => {
    const uom = uomCode(l.uomId) ? ` ${uomCode(l.uomId)}` : '';
    const coding = [l.costCenter, l.glAccountCode].filter(Boolean).join(' / ');
    const sub = [l.sku, coding].filter(Boolean).join(' · ');
    const reasonLabel = discrepancyReasonLabel(l.discrepancyReason);
    const issue = reasonLabel ? t(reasonLabel) : (l.overReceived ? t('Over-received') : null);
    const rejected = Number(l.rejectedQuantity || 0);
    return (
      <div className="grn-line" key={l.lineId}>
        <div className="grn-line__item">
          <div className="grn-line__name">{l.description}</div>
          {sub && <div className="grn-line__sub">{sub}</div>}
          {(issue || l.notes) && (
            <div className="grn-line__issue">
              {issue && <Badge tone="warning">{issue}</Badge>}
              {l.notes && <span className="grn-line__issue-note">“{l.notes}”</span>}
            </div>
          )}
          {(linePhotos[l.lineId] || []).length > 0 && (
            <div className="grn-line__photos">
              {linePhotos[l.lineId].map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="grn-photo"
                  title={p.fileName}
                  onClick={() => p.url && window.open(p.url, '_blank', 'noopener')}
                >
                  {p.url
                    ? <img src={p.url} alt={p.fileName} />
                    : <span className="grn-photo__fallback"><Icon.Image size={16} /></span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="grn-line__qty">
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Ordered')}</span>
            <span className="grn-line__stat-value">{l.orderedQuantity}{uom}</span>
          </div>
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Accepted')}</span>
            <span className="grn-line__stat-value">
              {l.receivedQuantity}{uom}{l.overReceived && <Badge tone="warning" style={{ marginLeft: 6 }}>{t('Over')}</Badge>}
            </span>
          </div>
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Rejected')}</span>
            <span className={`grn-line__stat-value${rejected > 0 ? ' grn-line__stat-value--reject' : ''}`}>
              {rejected > 0 ? `${l.rejectedQuantity}${uom}` : '—'}
            </span>
          </div>
          {l.hasPriceVariance && (
            <div className="grn-line__stat" title={t('The price on the delivery note differed from the committed PO price.')}>
              <span className="grn-line__stat-label">{t('Price')}</span>
              <span className="grn-line__stat-value">
                {money(l.unitPrice)} <Badge tone="warning" style={{ marginLeft: 6 }}>{t('PO {price}', { price: money(l.poUnitPrice) })}</Badge>
              </span>
            </div>
          )}
          <div className="grn-line__stat">
            <span className="grn-line__stat-label">{t('Value')}</span>
            <span className="grn-line__stat-value">{lineValue(l)}</span>
          </div>
        </div>
      </div>
    );
  };

  const activityItems = activity.map((a) => {
    if (a.type === 'Commented') {
      return buildCommentTimelineItem({
        activity: a,
        apiBasePath: `/goods-receipts/${grn.id}`,
        attachmentsBasePath: `/goods-receipts/${grn.id}/attachments`,
        currentUserId: user?.id,
        editingCommentId,
        setEditingCommentId,
        onChanged: () => loadActivity(grn.id),
        highlightId: anchorId,
        t,
      });
    }

    const meta = ACTIVITY_META[a.type] || { tone: 'default', icon: <Icon.Activity size={14} /> };
    const detail = a.metadata?.resolution
      || (a.type === 'Posted' ? t('{accepted} accepted · {rejected} rejected · {value}', { accepted: a.metadata?.accepted ?? '0', rejected: a.metadata?.rejected ?? '0', value: a.metadata?.value ?? '' }).trim() : undefined);
    return {
      id: a.id,
      tone: meta.tone,
      icon: meta.icon,
      title: `${a.actorName || t('Someone')} ${a.summary}`,
      meta: new Date(a.createdAt).toLocaleString(),
      description: detail || undefined,
    };
  });

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/receiving')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Receiving')}
      </Button>

      <PageHeader
        title={isDraft ? t('Draft goods receipt') : t('Goods receipt {number}', { number: grn.number })}
        description={<span className="req-detail__number">{grn.purchaseOrderNumber}</span>}
        icon={<Icon.ArrowDownCircle size={22} />}
        actions={headerActions}
      />

      {isDraft && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="info">
            {t('This receipt is a draft — nothing has been committed to stock, the purchase order or the budget yet. Edit it as needed, then post it to finalize.')}
          </Banner>
        </div>
      )}

      {isCancelled && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="danger">
            {t('This receipt was cancelled')}{grn.cancelledByName ? ` ${t('by {name}', { name: grn.cancelledByName })}` : ''}{grn.cancelledAt ? ` ${t('on {date}', { date: new Date(grn.cancelledAt).toLocaleString() })}` : ''}. {t('Stock, the purchase order and purchase history were reversed.')}{grn.cancelledReason ? ` ${t('Reason: {reason}', { reason: grn.cancelledReason })}` : ''}
          </Banner>
        </div>
      )}

      {grn.hasOverReceipt && !isCancelled && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="warning">{t('This receipt includes one or more over-received lines (more than the outstanding ordered quantity).')}</Banner>
        </div>
      )}

      {grn.discrepancyStatus === 'Open' && !isCancelled && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="danger">{t('This receipt has an open discrepancy awaiting supplier follow-up. Rejected goods were not added to stock and remain outstanding on the order.')}</Banner>
        </div>
      )}

      <div className="req-kpis">
        <KpiTile label={t('Received value')} value={money(grn.total)} />
        <KpiTile label={t('Lines')} value={grn.lineCount} />
        <KpiTile label={t('Supplier')} value={grn.supplierName || '—'} />
        <KpiTile label={t('Status')} value={t(statusMeta.label)} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Card>
          <Card.Body>
            <DescriptionList items={overviewItems} layout="stacked" columns={2} />
          </Card.Body>
        </Card>

        {grn.notes && (
          <Card>
            <Card.Header><Card.Title>{t('Notes')}</Card.Title></Card.Header>
            <Card.Body>{grn.notes}</Card.Body>
          </Card>
        )}

        {grn.discrepancyStatus && grn.discrepancyStatus !== 'None' && !isCancelled && (
          <Card>
            <Card.Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Card.Title>{t('Discrepancy')}</Card.Title>
              <Badge tone={grnDiscrepancyMeta(grn.discrepancyStatus).tone} dot>
                {t(grnDiscrepancyMeta(grn.discrepancyStatus).label)}
              </Badge>
            </Card.Header>
            <Card.Body>
              {grn.discrepancyStatus === 'Resolved' ? (
                <DescriptionList
                  layout="stacked"
                  columns={2}
                  items={[
                    { term: t('Resolved by'), description: grn.resolvedByName || '—' },
                    { term: t('Resolved at'), description: grn.resolvedAt ? new Date(grn.resolvedAt).toLocaleString() : '—' },
                    { term: t('Resolution'), description: grn.resolutionNotes || '—' },
                  ]}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  <div className="pp-cell-muted">
                    {t('Record how this was resolved with the supplier (credit note, replacement delivery, or accepted as-is), then mark it resolved.')}
                  </div>
                  <Textarea
                    rows={2}
                    value={resolveNote}
                    onChange={(e) => setResolveNote(e.target.value)}
                    placeholder={t('Resolution notes (e.g. credit note CN-123 received, replacement due Friday)…')}
                    disabled={!canResolve}
                  />
                  <div>
                    <Button
                      variant="primary"
                      size="sm"
                      loading={resolving}
                      disabled={!canResolve}
                      leadingIcon={<Icon.Check size={14} />}
                      onClick={resolveDiscrepancy}
                    >
                      {t('Mark resolved')}
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        <Card>
          <Card.Header>
            <Card.Title>{isDraft ? t('Lines') : t('Received lines')}</Card.Title>
            <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
              {isDraft
                ? t('Accepted quantities will be added to stock when this receipt is posted. Rejected quantities are recorded but never stocked.')
                : t('Accepted quantities were added to stock. Rejected quantities were not stocked and stay outstanding on the order.')}
            </div>
          </Card.Header>
          <Card.Body>
            <div className="grn-lines">
              {(grn.lines || []).map(renderLine)}
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <Card.Title>{t('Activity')}</Card.Title>
            <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
              {t('Audit trail for this receipt — who did what and when.')}
            </div>
          </Card.Header>
          <Card.Body>
            <CommentComposer
              basePath={`/goods-receipts/${grn.id}`}
              onPosted={() => loadActivity(grn.id)}
            />
            {activityItems.length === 0 ? (
              <EmptyState icon={<Icon.Activity size={26} />} title={t('No activity yet')} description={t('Posting and discrepancy events will appear here.')} />
            ) : (
              <Timeline items={activityItems} className="req-activity__timeline" />
            )}
          </Card.Body>
        </Card>
      </div>

      <ConfirmDialog
        open={postOpen}
        title={t('Post goods receipt')}
        description={t('Posting commits the accepted quantities to stock, advances the purchase order and writes purchase history. You can still cancel it afterwards if needed.')}
        confirmLabel={t('Post receipt')}
        tone="primary"
        loading={busy}
        onConfirm={doPost}
        onCancel={() => (busy ? null : setPostOpen(false))}
      />

      <ConfirmDialog
        open={discardOpen}
        title={t('Discard draft receipt')}
        description={t("Permanently delete this draft. Nothing has been committed to stock, so there's nothing to reverse. This can't be undone.")}
        confirmLabel={t('Discard draft')}
        tone="danger"
        loading={busy}
        onConfirm={doDiscard}
        onCancel={() => (busy ? null : setDiscardOpen(false))}
      />

      <ConfirmDialog
        open={cancelOpen}
        title={t('Cancel goods receipt')}
        description={t("This reverses the accepted stock, restores the purchase order's outstanding quantities and deletes the purchase-history records. If any of this stock has already been issued, the cancel will be blocked.")}
        confirmLabel={t('Cancel receipt')}
        tone="danger"
        loading={busy}
        onConfirm={doCancel}
        onCancel={() => (busy ? null : setCancelOpen(false))}
      >
        <Textarea
          rows={2}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder={t('Reason (optional) — e.g. posted against the wrong PO')}
        />
      </ConfirmDialog>
    </PageContainer>
  );
}
